<template>
    <box-view class="customer-module" title="Quản lý Khách hàng" :showBack="true" @close="$router.push('/admin')">
        <div class="container-fluid py-3">
            <!-- Header Actions -->
            <div class="row mb-4">
                <div class="col-12 d-flex justify-content-between align-items-center">
                    <div>
                        <h5 class="mb-0 text-secondary">
                            <i class="fas fa-user-friends mr-2"></i>
                            Tổng: <strong class="text-primary">{{ filteredCustomers.length }}</strong> / {{ customers.length }} khách hàng
                        </h5>
                    </div>
                    <button v-if="canManageCustomers" class="btn btn-primary btn-add" @click="openModal()">
                        <i class="fas fa-plus mr-2"></i> Thêm khách hàng
                    </button>
                </div>
            </div>

            <!-- Search Bar -->
            <div class="row mb-4">
                <div class="col-12">
                    <div class="search-box">
                        <i class="fas fa-search search-icon"></i>
                        <input 
                            type="text" 
                            class="form-control search-input" 
                            v-model="searchQuery" 
                            placeholder="Tìm kiếm theo tên, số điện thoại hoặc địa chỉ..."
                        />
                        <button v-if="searchQuery" class="btn-clear" @click="searchQuery = ''">
                            <i class="fas fa-times"></i>
                        </button>
                    </div>
                </div>
            </div>

            <!-- Customer Cards Grid -->
            <div class="row">
                <div v-for="cus in paginatedCustomers" :key="cus.id" class="col-12 col-md-6 col-lg-4 mb-4">
                    <div class="customer-card card h-100 border-0 shadow-sm">
                        <div class="card-body p-4">
                            <div class="d-flex align-items-start mb-3">
                                <div class="customer-avatar mr-3">
                                    <img :src="'https://ui-avatars.com/api/?name=' + encodeURIComponent(cus.name) + '&background=304ffe&color=fff&size=60'" 
                                         class="rounded-circle" width="60" height="60" alt="avatar" />
                                    <div v-if="cus.type === 'vip'" class="vip-badge">
                                        <i class="fas fa-crown"></i>
                                    </div>
                                </div>
                                <div class="flex-grow-1">
                                    <h5 class="mb-1 font-weight-bold">{{ cus.name }}</h5>
                                    <div class="text-muted small mb-2">
                                        <i class="fas fa-phone mr-1"></i> {{ cus.phone }}
                                    </div>
                                    <span class="badge badge-pill" :class="cus.type === 'vip' ? 'badge-warning' : (cus.type === 'agent' ? 'badge-info' : 'badge-secondary')">
                                        {{ cus.type === 'vip' ? 'Khách VIP' : (cus.type === 'agent' ? 'Đại lý' : (cus.type === 'normal' ? 'Khách thường' : cus.type)) }}
                                    </span>
                                </div>
                            </div>

                            <!-- Customer Info -->
                            <div class="customer-info mb-3">
                                <div class="info-item" v-if="cus.address">
                                    <i class="fas fa-map-marker-alt text-muted mr-2"></i>
                                    <span class="text-truncate">{{ cus.address }}</span>
                                </div>
                                <div class="info-item debt-info" :class="{ 'has-debt': Math.round(cus.debt_amount) > 0 }">
                                    <span class="info-label">Công nợ:</span>
                                    <span class="debt-amount">{{ (cus.debt_amount || 0) | formatCurrency }}</span>
                                </div>
                            </div>

                            <!-- Actions -->
                            <div v-if="canManageCustomers" class="customer-actions d-flex gap-2">
                                <button class="btn btn-sm btn-outline-primary flex-fill" @click="openModal(cus)">
                                    <i class="fas fa-edit mr-1"></i> Sửa
                                </button>
                                <button class="btn btn-sm btn-outline-danger" @click="handleDelete(cus.id)">
                                    <i class="fas fa-trash"></i>
                                </button>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- Empty State -->
                <div v-if="filteredCustomers.length === 0 && searchQuery" class="col-12">
                    <div class="empty-state text-center py-5">
                        <i class="fas fa-search fa-4x text-muted mb-3"></i>
                        <h5 class="text-muted">Không tìm thấy kết quả</h5>
                        <p class="text-muted">Thử tìm kiếm với từ khóa khác</p>
                    </div>
                </div>
                <div v-else-if="customers.length === 0" class="col-12">
                    <div class="empty-state text-center py-5">
                        <i class="fas fa-user-friends fa-4x text-muted mb-3"></i>
                        <h5 class="text-muted">Chưa có khách hàng nào</h5>
                        <p class="text-muted">Hãy thêm khách hàng đầu tiên để bắt đầu</p>
                    </div>
                </div>
            </div>

        <!-- PAGINATION -->
        <nav v-if="totalPages > 1" class="mt-3">
            <ul class="pagination pagination-sm justify-content-center">
                <li class="page-item" :class="{ disabled: currentPage === 1 }">
                    <a class="page-link" href="#" @click.prevent="currentPage--">«</a>
                </li>
                <li v-for="page in visiblePages" :key="'page-'+page" class="page-item" :class="{ active: page === currentPage }">
                    <a class="page-link" href="#" @click.prevent="currentPage = page">{{ page }}</a>
                </li>
                <li class="page-item" :class="{ disabled: currentPage === totalPages }">
                    <a class="page-link" href="#" @click.prevent="currentPage++">»</a>
                </li>
            </ul>
        </nav>
        </div>

        <!-- Modal Thêm / Sửa -->
        <van-dialog v-model="showModal" :title="editing ? 'Sửa thông tin khách hàng' : 'Thêm khách hàng mới'" show-cancel-button @confirm="save">
            <div class="p-3">
                <div class="form-group">
                    <label class="font-weight-bold"><i class="fas fa-user mr-1"></i> Tên khách hàng</label>
                    <input type="text" class="form-control" v-model="form.name" placeholder="Nhập tên">
                </div>
                <div class="form-group">
                    <label class="font-weight-bold"><i class="fas fa-phone mr-1"></i> Số điện thoại</label>
                    <input type="text" class="form-control" v-model="form.phone" placeholder="Nhập SĐT">
                </div>
                <div class="form-group">
                    <label class="font-weight-bold"><i class="fas fa-map-marker-alt mr-1"></i> Địa chỉ</label>
                    <input type="text" class="form-control" v-model="form.address" placeholder="Nhập địa chỉ">
                </div>
                <div class="form-group">
                    <label class="font-weight-bold"><i class="fas fa-star mr-1"></i> Loại khách hàng</label>
                    <select class="form-control" v-model="form.type">
                        <option v-for="type in customerTypes" :key="type" :value="type">
                            {{ type === 'normal' ? 'Khách thường' : (type === 'vip' ? 'Khách VIP' : (type === 'agent' ? 'Đại lý' : type)) }}
                        </option>
                    </select>
                </div>
                <div class="form-group">
                    <label class="font-weight-bold"><i class="fas fa-sticky-note mr-1"></i> Ghi chú</label>
                    <input type="text" class="form-control" v-model="form.note" placeholder="Ghi chú thêm">
                </div>
            </div>
        </van-dialog>
    </box-view>
</template>

<script>
export default {
    name: 'Customer',
    props: {
        debts: {
            type: Array,
            default: () => []
        },
        user: {
            type: Object,
            default: () => ({})
        },
        settings: {
            type: Object,
            default: () => ({})
        }
    },
    data() {
        return {
            searchQuery: '',
            showModal: false,
            editing: false,
            loading: false,
            customers: [],
            totalCustomers: 0,
            form: {
                id: null,
                name: '',
                phone: '',
                address: '',
                type: 'normal',
                note: ''
            },
            currentPage: 1,
            pageSize: 24
        };
    },
    computed: {
        filteredCustomers() {
            // Logic lọc client-side tạm thời (nếu muốn) hoặc dùng trực tiếp list từ API
            // Ở đây vì có search API, ta hiển thị customers trực tiếp
            return this.customers;
        },
        totalPages() {
            return Math.ceil(this.filteredCustomers.length / this.pageSize);
        },
        visiblePages() {
            const pages = [];
            const maxPagesToShow = 5;
            let start = Math.max(1, this.currentPage - Math.floor(maxPagesToShow / 2));
            let end = Math.min(this.totalPages, start + maxPagesToShow - 1);
            if (end - start + 1 < maxPagesToShow) {
                start = Math.max(1, end - maxPagesToShow + 1);
            }
            for (let i = start; i <= end; i++) {
                pages.push(i);
            }
            return pages;
        },
        paginatedCustomers() {
            const start = (this.currentPage - 1) * this.pageSize;
            return this.filteredCustomers.slice(start, start + this.pageSize);
        },
        canManageCustomers() {
            const role = this.user.role || 'admin';
            if(role === 'admin') return true;
            const roleConfig = this.settings.user?.roles?.[role];
            return roleConfig?.permissions?.includes('manageCustomers');
        },
        customerTypes() {
            // Get from Global Settings via Root
            return this.$root.settings?.customer?.customerTypes || ['normal', 'vip', 'agent'];
        }
    },
    mounted() {
        this.fetchCustomers();
    },
    methods: {
        async fetchCustomers() {
            this.loading = true;
            try {
                const res = await this.$root.api.get('/logach/getLoGachCustomers', {
                    params: { key: this.searchQuery, per: 5000 }
                });
                // Check multiple success indicators to be safe
                if (res.data && (res.data.code === 200 || res.data.status === 'OK' || res.data.status === 'success')) {
                    // Try data.list first (standard), fallback to data.data.list (my previous assumption)
                    if (res.data.list) {
                        this.customers = res.data.list;
                        this.totalCustomers = res.data.total || res.data.list.length;
                    } else if (res.data.data && res.data.data.list) {
                        this.customers = res.data.data.list;
                        this.totalCustomers = res.data.data.total;
                    }
                }
            } catch (e) {
                console.error(e);
            } finally {
                this.loading = false;
            }
        },
        openModal(customer = null) {
            if (customer) {
                this.editing = true;
                this.form = { ...customer };
            } else {
                this.editing = false;
                this.form = {
                    id: null,
                    name: '',
                    phone: '',
                    address: '',
                    type: 'normal',
                    note: ''
                };
            }
            this.showModal = true;
        },

        async save() {
            if (!this.form.name || !this.form.phone) {
                this.$toast?.fail('Vui lòng nhập tên và số điện thoại');
                return;
            }

            try {
                const res = await this.$root.api.post('/logach/postSaveLoGachCustomer', this.form);
                if (res.data && (res.data.code === 200 || res.data.status === 'OK')) {
                    this.$toast?.success(this.editing ? 'Cập nhật thành công' : 'Thêm mới thành công');
                    this.fetchCustomers();
                    this.$emit('refresh-global');
                    this.showModal = false;
                } else {
                    this.$toast?.fail(res.data?.message || 'Lỗi lưu dữ liệu');
                }
            } catch (e) {
                this.$toast?.fail('Lỗi kết nối');
            }
        },

        async handleDelete(id) {
            if (confirm('Bạn có chắc muốn xóa khách hàng này?')) {
                try {
                    const res = await this.$root.api.post('/logach/postDeleteLoGachCustomer', { id });
                    if (res.data && (res.data.code === 200 || res.data.status === 'OK')) {
                         this.$toast?.success('Đã xóa khách hàng');
                         this.fetchCustomers();
                         this.$emit('refresh-global');
                    } else {
                         this.$toast?.fail(res.data?.message || 'Lỗi xóa');
                    }
                } catch (e) {
                    this.$toast?.fail('Lỗi kết nối');
                }
            }
        },

        getDebt(customerId) {
            // Updated to use the property directly in template
            // This method might be deprecated if we use direct property in v-for
            return 0; 
        }
    },
    watch: {
        searchQuery() {
            // Debounce simple
            clearTimeout(this._timer);
            this._timer = setTimeout(() => {
                this.currentPage = 1;
                this.fetchCustomers();
            }, 500);
        }
    }
};
</script>

